/**
 * Analytics Service for tracking visitors, sessions, and generating reports
 * Uses Firebase Firestore for real-time data storage
 */
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDocs, 
  setDoc,
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from './config';

// Collection names
const VISITORS_COLLECTION = 'active_visitors';
const SESSIONS_COLLECTION = 'visitor_sessions';
const PAGE_VIEWS_COLLECTION = 'page_views';
const ANALYTICS_DAILY_COLLECTION = 'analytics_daily';

/**
 * Get user's location from IP using free geolocation APIs with fallbacks
 */
export const getUserLocation = async () => {
  // Try multiple APIs in order until one works
  const apis = [
    // API 1: ipapi.co with different endpoint (supports CORS)
    async () => {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: { 
          'Accept': 'application/json',
          'User-Agent': 'Mozilla/5.0'
        }
      });
      if (!response.ok) throw new Error('ipapi.co failed');
      const data = await response.json();
      return {
        ip: data.ip,
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        regionCode: data.region_code || 'Unknown',
        country: data.country_name || 'Unknown',
        countryCode: data.country_code || 'Unknown',
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone,
        org: data.org
      };
    },
    // API 2: ipify + ipapi (two-step: get IP, then get location)
    async () => {
      // First get IP from ipify (very reliable)
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      if (!ipResponse.ok) throw new Error('ipify failed');
      const ipData = await ipResponse.json();
      
      // Then get location from ip-api.com using HTTPS
      const response = await fetch(`https://pro.ip-api.com/json/${ipData.ip}?key=free`);
      if (!response.ok) throw new Error('ip-api.com failed');
      const data = await response.json();
      if (data.status === 'fail') throw new Error('ip-api.com returned fail status');
      return {
        ip: data.query || ipData.ip,
        city: data.city || 'Unknown',
        region: data.regionName || 'Unknown',
        regionCode: data.region || 'Unknown',
        country: data.country || 'Unknown',
        countryCode: data.countryCode || 'Unknown',
        latitude: data.lat,
        longitude: data.lon,
        timezone: data.timezone,
        org: data.isp
      };
    },
    // API 3: freeipapi.com (free, CORS-enabled)
    async () => {
      const response = await fetch('https://freeipapi.com/api/json');
      if (!response.ok) throw new Error('freeipapi.com failed');
      const data = await response.json();
      return {
        ip: data.ipAddress,
        city: data.cityName || 'Unknown',
        region: data.regionName || 'Unknown',
        regionCode: data.regionName || 'Unknown',
        country: data.countryName || 'Unknown',
        countryCode: data.countryCode || 'Unknown',
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timeZone,
        org: data.isProxy ? 'Proxy' : 'Unknown'
      };
    }
  ];

  // Try each API until one succeeds
  for (let i = 0; i < apis.length; i++) {
    try {
      const locationData = await apis[i]();
      console.log(`Location fetched successfully using API ${i + 1}`);
      return locationData;
    } catch (error) {
      console.warn(`API ${i + 1} failed:`, error.message);
      // Continue to next API
    }
  }

  // If all APIs fail, return default values
  console.warn('All geolocation APIs failed, using default location');
  return {
    ip: 'Unknown',
    city: 'Unknown',
    region: 'Unknown',
    regionCode: 'Unknown',
    country: 'Unknown',
    countryCode: 'Unknown',
    latitude: null,
    longitude: null,
    timezone: null,
    org: null
  };
};

/**
 * Generate a unique session ID
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get or create a visitor session
 */
export const initVisitorSession = async (userId = null) => {
  try {
    // Check if session already exists in sessionStorage
    let sessionId = sessionStorage.getItem('visitorSessionId');
    
    if (!sessionId) {
      sessionId = generateSessionId();
      sessionStorage.setItem('visitorSessionId', sessionId);
    }
    
    // Get user location
    const location = await getUserLocation();
    
    // Get device/browser info
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      isMobile: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent),
      isTablet: /iPad|Android(?!.*Mobile)/i.test(navigator.userAgent),
      browser: getBrowserName(),
      os: getOSName()
    };
    
    // Create or update active visitor record using setDoc with merge
    const visitorRef = doc(db, VISITORS_COLLECTION, sessionId);
    
    const visitorData = {
      sessionId,
      userId: userId || null,
      location,
      deviceInfo,
      currentPage: window.location.pathname,
      lastActive: serverTimestamp(),
      isActive: true,
      startTime: serverTimestamp(),
      pageViews: 1
    };
    
    // Use setDoc with merge to create or update
    await setDoc(visitorRef, visitorData, { merge: true });
    
    console.log('Visitor session initialized:', sessionId);
    
    // Also create a session record for historical tracking
    const sessionsRef = collection(db, SESSIONS_COLLECTION);
    const existingSession = await getDocs(
      query(sessionsRef, where('sessionId', '==', sessionId), limit(1))
    );
    
    if (existingSession.empty) {
      await addDoc(sessionsRef, {
        sessionId,
        userId: userId || null,
        location,
        deviceInfo,
        startTime: serverTimestamp(),
        lastActive: serverTimestamp(),
        pageViews: 1,
        pages: [window.location.pathname],
        referrer: document.referrer || 'direct',
        utmSource: new URLSearchParams(window.location.search).get('utm_source') || null,
        utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || null,
        utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || null
      });
    }
    
    // Set up heartbeat to keep session active
    startHeartbeat(sessionId);
    
    // Set up cleanup on page unload
    window.addEventListener('beforeunload', () => {
      endVisitorSession(sessionId);
    });
    
    return sessionId;
  } catch (error) {
    console.error('Error initializing visitor session:', error);
    return null;
  }
};

/**
 * Get browser name from user agent
 */
const getBrowserName = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('MSIE') || ua.includes('Trident')) return 'Internet Explorer';
  return 'Unknown';
};

/**
 * Get OS name from user agent
 */
const getOSName = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Linux') && !ua.includes('Android')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

/**
 * Start heartbeat to keep session active
 */
let heartbeatInterval = null;
const startHeartbeat = (sessionId) => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }
  
  heartbeatInterval = setInterval(async () => {
    try {
      const visitorRef = doc(db, VISITORS_COLLECTION, sessionId);
      await updateDoc(visitorRef, {
        lastActive: serverTimestamp(),
        currentPage: window.location.pathname
      });
    } catch (error) {
      console.error('Heartbeat error:', error);
    }
  }, 30000); // Update every 30 seconds
};

/**
 * End visitor session (called on page unload)
 */
export const endVisitorSession = async (sessionId) => {
  try {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }
    
    const visitorRef = doc(db, VISITORS_COLLECTION, sessionId);
    await updateDoc(visitorRef, {
      isActive: false,
      endTime: serverTimestamp()
    });
  } catch (error) {
    console.error('Error ending session:', error);
  }
};

/**
 * Track page view
 */
export const trackPageView = async (pagePath, pageTitle = '') => {
  try {
    const sessionId = sessionStorage.getItem('visitorSessionId');
    if (!sessionId) return;
    
    // Update active visitor's current page
    const visitorRef = doc(db, VISITORS_COLLECTION, sessionId);
    await updateDoc(visitorRef, {
      currentPage: pagePath,
      lastActive: serverTimestamp(),
      pageViews: increment(1)
    });
    
    // Log page view
    await addDoc(collection(db, PAGE_VIEWS_COLLECTION), {
      sessionId,
      pagePath,
      pageTitle,
      timestamp: serverTimestamp(),
      referrer: document.referrer
    });
    
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Subscribe to active visitors (real-time updates)
 */
export const subscribeToActiveVisitors = (callback) => {
  const visitorsRef = collection(db, VISITORS_COLLECTION);
  
  // Simple query - just get all active visitors, filter by time client-side
  const q = query(
    visitorsRef,
    where('isActive', '==', true)
  );
  
  return onSnapshot(q, (snapshot) => {
    const fiveMinutesAgo = new Date();
    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
    
    const visitors = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // Filter by lastActive client-side to avoid composite index requirement
      const lastActive = data.lastActive?.toDate?.() || new Date(0);
      if (lastActive > fiveMinutesAgo) {
        visitors.push({ id: docSnap.id, ...data });
      }
    });
    console.log('Active visitors updated:', visitors.length);
    callback(visitors);
  }, (error) => {
    console.error('Error subscribing to visitors:', error);
    callback([]);
  });
};

/**
 * Clean up stale visitor sessions (run periodically)
 */
export const cleanupStaleVisitors = async () => {
  try {
    const fiveMinutesAgo = new Date();
    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
    
    const visitorsRef = collection(db, VISITORS_COLLECTION);
    const q = query(
      visitorsRef,
      where('lastActive', '<', Timestamp.fromDate(fiveMinutesAgo))
    );
    
    const snapshot = await getDocs(q);
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    console.log(`Cleaned up ${snapshot.size} stale visitor sessions`);
  } catch (error) {
    console.error('Error cleaning up stale visitors:', error);
  }
};

/**
 * Get analytics summary for a date range
 */
export const getAnalyticsSummary = async (startDate, endDate) => {
  try {
    const sessionsRef = collection(db, SESSIONS_COLLECTION);
    const q = query(
      sessionsRef,
      where('startTime', '>=', Timestamp.fromDate(startDate)),
      where('startTime', '<=', Timestamp.fromDate(endDate)),
      orderBy('startTime', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const sessions = [];
    snapshot.forEach((doc) => {
      sessions.push({ id: doc.id, ...doc.data() });
    });
    
    // Calculate metrics
    const totalSessions = sessions.length;
    const uniqueVisitors = new Set(sessions.map(s => s.sessionId)).size;
    const totalPageViews = sessions.reduce((sum, s) => sum + (s.pageViews || 1), 0);
    const avgSessionDuration = calculateAvgSessionDuration(sessions);
    const bounceRate = calculateBounceRate(sessions);
    
    // Location breakdown
    const locationBreakdown = {};
    sessions.forEach(session => {
      const state = session.location?.regionCode || session.location?.region || 'Unknown';
      locationBreakdown[state] = (locationBreakdown[state] || 0) + 1;
    });
    
    // Device breakdown
    const deviceBreakdown = { desktop: 0, mobile: 0, tablet: 0 };
    sessions.forEach(session => {
      if (session.deviceInfo?.isTablet) {
        deviceBreakdown.tablet++;
      } else if (session.deviceInfo?.isMobile) {
        deviceBreakdown.mobile++;
      } else {
        deviceBreakdown.desktop++;
      }
    });
    
    // Browser breakdown
    const browserBreakdown = {};
    sessions.forEach(session => {
      const browser = session.deviceInfo?.browser || 'Unknown';
      browserBreakdown[browser] = (browserBreakdown[browser] || 0) + 1;
    });
    
    // Traffic sources
    const trafficSources = { direct: 0, organic: 0, referral: 0, social: 0, paid: 0 };
    sessions.forEach(session => {
      const source = categorizeTrafficSource(session.referrer, session.utmSource, session.utmMedium);
      trafficSources[source]++;
    });
    
    return {
      totalSessions,
      uniqueVisitors,
      totalPageViews,
      avgSessionDuration,
      bounceRate,
      locationBreakdown,
      deviceBreakdown,
      browserBreakdown,
      trafficSources,
      sessions
    };
  } catch (error) {
    console.error('Error getting analytics summary:', error);
    return null;
  }
};

/**
 * Calculate average session duration
 */
const calculateAvgSessionDuration = (sessions) => {
  const validSessions = sessions.filter(s => s.startTime && s.lastActive);
  if (validSessions.length === 0) return 0;
  
  const totalDuration = validSessions.reduce((sum, session) => {
    const start = session.startTime?.toDate?.() || new Date(session.startTime);
    const end = session.lastActive?.toDate?.() || new Date(session.lastActive);
    return sum + (end - start);
  }, 0);
  
  return Math.round(totalDuration / validSessions.length / 1000); // Return in seconds
};

/**
 * Calculate bounce rate (sessions with only 1 page view)
 */
const calculateBounceRate = (sessions) => {
  if (sessions.length === 0) return 0;
  const bouncedSessions = sessions.filter(s => (s.pageViews || 1) === 1).length;
  return Math.round((bouncedSessions / sessions.length) * 100);
};

/**
 * Categorize traffic source
 */
const categorizeTrafficSource = (referrer, utmSource, utmMedium) => {
  if (utmMedium === 'cpc' || utmMedium === 'ppc' || utmMedium === 'paid') {
    return 'paid';
  }
  
  if (!referrer || referrer === 'direct') {
    return 'direct';
  }
  
  const socialDomains = ['facebook.com', 'twitter.com', 'instagram.com', 'linkedin.com', 'pinterest.com', 'tiktok.com'];
  const searchEngines = ['google.com', 'bing.com', 'yahoo.com', 'duckduckgo.com', 'baidu.com'];
  
  for (const domain of socialDomains) {
    if (referrer.includes(domain)) return 'social';
  }
  
  for (const domain of searchEngines) {
    if (referrer.includes(domain)) return 'organic';
  }
  
  return 'referral';
};

/**
 * Get top pages by views
 */
export const getTopPages = async (startDate, endDate, limitCount = 10) => {
  try {
    const pageViewsRef = collection(db, PAGE_VIEWS_COLLECTION);
    const q = query(
      pageViewsRef,
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<=', Timestamp.fromDate(endDate)),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const pageCounts = {};
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const path = data.pagePath;
      pageCounts[path] = (pageCounts[path] || 0) + 1;
    });
    
    const sortedPages = Object.entries(pageCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limitCount)
      .map(([path, count]) => ({ path, views: count }));
    
    return sortedPages;
  } catch (error) {
    console.error('Error getting top pages:', error);
    return [];
  }
};

export default {
  initVisitorSession,
  endVisitorSession,
  trackPageView,
  subscribeToActiveVisitors,
  cleanupStaleVisitors,
  getAnalyticsSummary,
  getTopPages,
  getUserLocation
};
